import type { CaptureResult } from "posthog-js";
import posthog from "posthog-js";
// The bundler inlines this; the release flow runs `yarn version` before build, so it is the exact
// published version. Use `version` (NOT `stableVersion`).
import { version as PKJS_VERSION } from "../../package.json";
import type { AnalyticsAdapter } from "./types";

// PostHog API key (PaymentKit internal)
const POSTHOG_API_KEY = "phc_u9YDYUeUxo3CYn16XGrrwpVb8ycXgcjz5DarvIKRAAG";

// --- PII scrubbing for outgoing events (error tracking is on) ----------------------
// PKJS runs inside merchant checkout. Card data never reaches JS (it lives in the VGS
// iframes), but an exception message, a stack frame, or a stray event property can
// still carry a shopper's email / phone / account number. The before_send hook below
// redacts those before anything leaves the browser. Deliberately small and defensive:
// it must never throw, or it would drop the event / break capture.

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// 7+ consecutive digits: phone numbers, account/card-ish numbers. Our ids
// (external_id, request_id, uuids) are alphanumeric, never all-digit, so they survive.
const LONG_DIGITS_RE = /\d{7,}/g;

const scrubString = (value: string): string =>
  value.replace(EMAIL_RE, "[redacted-email]").replace(LONG_DIGITS_RE, "[redacted-number]");

// Depth-bounded deep scrub of every string in a value. One pass covers
// $exception_message, the $exception_list stack frames, and arbitrary string props.
const MAX_SCRUB_DEPTH = 6;
const scrubValue = (value: unknown, depth: number): unknown => {
  if (depth >= MAX_SCRUB_DEPTH) return value;
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value)) out[key] = scrubValue(v, depth + 1);
    return out;
  }
  return value;
};

/**
 * PostHog `before_send` hook: redacts obvious PII (emails, long digit sequences) from
 * an outgoing event's properties. Exported for unit testing. Never throws — on any
 * failure it returns the event untouched so capture still proceeds.
 */
export const scrubPiiFromEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  try {
    if (event.properties) {
      event.properties = scrubValue(event.properties, 0) as CaptureResult["properties"];
    }
  } catch {
    // Never throw from before_send.
  }
  return event;
};

/**
 * PostHog implementation of the analytics adapter.
 *
 * Singleton stored in window to ensure only one instance exists across
 * all script loads. Configures PostHog to use the reverse proxy at
 * /ingest to avoid ad blockers.
 */
export class PostHogAdapter implements AnalyticsAdapter {
  private initialized = false;

  /**
   * Get or create the PostHog adapter singleton (stored in window).
   *
   * @param apiBaseUrl - The API base URL for the reverse proxy
   * @throws Error if called with a different apiBaseUrl than the first call
   */
  static getInstance(apiBaseUrl: string): PostHogAdapter {
    if (typeof window === "undefined") {
      throw new Error("[PaymentKit Analytics] PostHog requires a browser environment");
    }

    const existing = window.__paymentKitPostHog__;
    if (existing) {
      if (existing.apiBaseUrl !== apiBaseUrl) {
        throw new Error(
          `[PaymentKit Analytics] PostHog already initialized with apiBaseUrl "${existing.apiBaseUrl}", ` +
            `cannot re-initialize with different apiBaseUrl "${apiBaseUrl}"`,
        );
      }
      return existing.instance;
    }

    const instance = new PostHogAdapter(apiBaseUrl);
    window.__paymentKitPostHog__ = { instance, apiBaseUrl };
    return instance;
  }

  private constructor(apiBaseUrl: string) {
    this.initPostHog(apiBaseUrl);
  }

  private initPostHog(apiBaseUrl: string): void {
    try {
      posthog.init(POSTHOG_API_KEY, {
        // Use reverse proxy to avoid ad blockers
        api_host: `${apiBaseUrl}/ingest`,
        // PostHog UI host (for toolbar, if ever needed)
        ui_host: "https://us.posthog.com",
        // Memory persistence - no cookies in embedded checkout iframe
        persistence: "memory",
        // Disable session recording - not needed for metrics
        disable_session_recording: true,
        // Manual events only - no autocapture
        autocapture: false,
        // Error tracking: capture unhandled exceptions/rejections from PKJS.
        // before_send scrubs PII from every outgoing event (incl. these $exception events).
        capture_exceptions: true,
        before_send: scrubPiiFromEvent,
        // Manual pageview control
        capture_pageview: false,
        // Disable batching - send events immediately to avoid data loss on page close
        request_batching: false,
        // Disable bot detection - allows events from automated tests and debugging environments
        // PostHog detects Playwright/headless browsers as bots by default
        opt_out_useragent_filter: true,
        // Callback when PostHog is ready
        loaded: () => {
          this.initialized = true;
        },
      });
      // Stamp the SDK version on EVERY event as a super property, so we can A/B PKJS versions and
      // confirm a new version reduces session_config_fetch_failed. Registered synchronously here
      // (not in the async `loaded` callback) so it is set before the first capture — both
      // checkout_page_ready and session_config_fetch_failed carry it.
      posthog.register({ pkjs_version: PKJS_VERSION });
    } catch (e) {
      console.warn("[PaymentKit Analytics] Failed to initialize PostHog:", e);
    }
  }

  identify(distinctId: string): void {
    try {
      posthog.identify(distinctId);
    } catch (e) {
      // Silent fail - never break checkout
      console.warn("[PaymentKit Analytics] Failed to identify:", e);
    }
  }

  capture(event: string, properties?: Record<string, unknown>): void {
    try {
      posthog.capture(event, properties);
    } catch (e) {
      // Silent fail - never break checkout
      console.warn("[PaymentKit Analytics] Failed to capture event:", e);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
